import type { ReactNode } from "react";

import { Hero } from "./Hero.tsx";
import { PhoneShell, Rows } from "./PhoneShell.tsx";

/**
 * The session that cannot be renewed, which the design does not draw.
 *
 * It matters more on this channel than on a console, and differently from the driver application's.
 * A courier whose session ages out stops receiving offers; a **rider** whose session ages out may be
 * in a car, on the way to an airport, with a trip in progress and no way to see it. The one thing
 * this screen must not do is look like a screen that is still following the ride.
 *
 * `lastedMs` and `endedAt` come from the authority rather than from a clock here, so the figures are
 * the session's own.
 */
export function SessionEnded({
  endedAt,
  lastedMs,
  onSignInAgain,
}: {
  readonly endedAt: number;
  readonly lastedMs: number;
  readonly onSignInAgain: () => void;
}): ReactNode {
  const ended = new Date(endedAt);
  const clock = `${String(ended.getHours()).padStart(2, "0")}:${String(ended.getMinutes()).padStart(2, "0")}`;
  const hours = Math.floor(lastedMs / 3_600_000);
  const minutes = Math.round((lastedMs % 3_600_000) / 60_000);
  const lasted = hours > 0 ? `${String(hours)} h ${String(minutes)} min` : `${String(minutes)} min`;

  return (
    <PhoneShell
      screen="journey screen--state"
      hero={<Hero kind="none" />}
      chip={{ class: "closed", label: "session ended" }}
      title="You've been signed out"
      sub="Your ride isn't cancelled and nothing is lost, but this app can't follow it until you sign in again."
      actions={[{ label: "Sign in again", glyph: "login", primary: true, onPress: onSignInAgain }]}
      flag="wording not in the design"
    >
      <Rows
        rows={[
          { key: "Ended at", value: clock },
          { key: "Lasted", value: lasted },
        ]}
      />
    </PhoneShell>
  );
}
